import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useConfirm } from '@/components/ui/confirm';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { ScheduleEditor } from '@/Pages/Catalog/menus/ScheduleEditor';
import { apiError, type ScheduleWindow, type StorefrontBranch, type UpsertStorefrontBranchInput } from './storefrontLib';

interface BranchRow {
  id: string;
  name: string;
  timezone?: string;
  isActive?: boolean;
}

/**
 * Which branches this storefront publishes, and the terms each is published
 * on: hours, pickup lead time, the pause switch. `serviceHours` reuses
 * `ScheduleEditor` from Catalog's menu builder — same `MenuScheduleWindowDto`
 * shape, same "empty means open all day" reading, one implementation of
 * "is this window open right now" rather than a second one drifting here.
 *
 * `deliveryEnabled` is shown but always refused by the backend (§16/ADR-0022
 * own-delivery is not built) — the toggle stays visible with an explanation
 * rather than vanishing, so an operator who goes looking for it finds out
 * why it does nothing instead of concluding the screen is broken.
 */
export default function BranchesTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const confirmDialog = useConfirm();
  const canUpdate = can('storefront.update');

  const [branches, setBranches] = useState<BranchRow[]>([]);
  const [rows, setRows] = useState<StorefrontBranch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<{ branchId: string; row: StorefrontBranch | null } | null>(null);
  const [form, setForm] = useState<UpsertStorefrontBranchInput>({ branchId: '' });
  const [hours, setHours] = useState<ScheduleWindow[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get('/branches', { params: { limit: 200 } }),
      api.get<StorefrontBranch[]>('/storefront/admin/branches'),
    ])
      .then(([branchesRes, storefrontRes]) => {
        setBranches((branchesRes.data?.data ?? branchesRes.data ?? []) as BranchRow[]);
        setRows(storefrontRes.data ?? []);
      })
      .catch((e) => setError(apiError(e, t('storefront.branches.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(load, [load]);

  const openEdit = (branch: BranchRow) => {
    const row = rows.find((r) => r.branchId === branch.id) ?? null;
    setEditing({ branchId: branch.id, row });
    setForm({
      branchId: branch.id,
      isPublished: row?.isPublished ?? true,
      acceptingOrders: row?.acceptingOrders ?? true,
      pickupEnabled: row?.pickupEnabled ?? true,
      deliveryEnabled: row?.deliveryEnabled ?? false,
      pickupLeadMinutes: row?.pickupLeadMinutes ?? 20,
    });
    setHours(row?.serviceHours ?? []);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/storefront/admin/branches', { ...form, serviceHours: hours });
      toast.success(t('storefront.branches.saved'));
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e, t('storefront.branches.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (branch: BranchRow) => {
    const ok = await confirmDialog.confirm({
      title: t('storefront.branches.removeConfirmTitle', { name: branch.name }),
      body: t('storefront.branches.removeConfirmBody'),
      confirmLabel: t('common.remove'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/storefront/admin/branches/${branch.id}`);
      toast.success(t('storefront.branches.removed'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('storefront.branches.removeFailed')));
    }
  };

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (branches.length === 0) {
    return <EmptyState title={t('storefront.branches.noBranches')} hint={t('storefront.branches.noBranchesHint')} />;
  }

  return (
    <div className="space-y-2">
      {branches.map((branch) => {
        const row = rows.find((r) => r.branchId === branch.id);
        return (
          <Card key={branch.id}>
            <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="font-medium">{branch.name}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {row ? (
                    <>
                      <Badge tone={row.isPublished ? 'success' : 'neutral'}>
                        {t(row.isPublished ? 'storefront.branches.published' : 'storefront.branches.unpublished')}
                      </Badge>
                      {row.isPublished && (
                        <Badge tone={row.acceptingOrders ? 'success' : 'danger'}>
                          {t(row.acceptingOrders ? 'storefront.branches.accepting' : 'storefront.branches.paused')}
                        </Badge>
                      )}
                    </>
                  ) : (
                    <Badge tone="neutral">{t('storefront.branches.notOnStorefront')}</Badge>
                  )}
                </div>
              </div>
              {canUpdate && (
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" onClick={() => openEdit(branch)}>
                    {t(row ? 'common.edit' : 'storefront.branches.publish')}
                  </Button>
                  {row && (
                    <Button variant="ghost" size="sm" onClick={() => void remove(branch)}>
                      {t('storefront.branches.remove')}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Dialog
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={t('storefront.branches.editTitle')}
        className="max-w-2xl"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={saving} onClick={() => void save()}>
              {t('common.save')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isPublished ?? true}
              onChange={(e) => setForm((f) => ({ ...f, isPublished: e.target.checked }))}
              className="h-4 w-4"
            />
            {t('storefront.branches.isPublished')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.acceptingOrders ?? true}
              onChange={(e) => setForm((f) => ({ ...f, acceptingOrders: e.target.checked }))}
              className="h-4 w-4"
            />
            {t('storefront.branches.acceptingOrders')}
            <span className="text-xs text-ink-muted">{t('storefront.branches.acceptingOrdersHint')}</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.pickupEnabled ?? true}
              onChange={(e) => setForm((f) => ({ ...f, pickupEnabled: e.target.checked }))}
              className="h-4 w-4"
            />
            {t('storefront.branches.pickupEnabled')}
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-muted">
            <input type="checkbox" checked={false} disabled className="h-4 w-4" />
            {t('storefront.branches.deliveryDisabled')}
          </label>

          <Field label={t('storefront.branches.pickupLeadMinutes')}>
            <Input
              type="number"
              min={0}
              max={480}
              value={form.pickupLeadMinutes ?? 20}
              onChange={(e) => setForm((f) => ({ ...f, pickupLeadMinutes: Number(e.target.value) }))}
            />
          </Field>

          <div>
            <p className="mb-1.5 text-sm font-medium">{t('storefront.branches.serviceHours')}</p>
            <ScheduleEditor schedule={hours} onChange={setHours} />
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => setHours((h) => [...h, { days: [0, 1, 2, 3, 4, 5, 6], from: '09:00', to: '22:00' }])}
            >
              {t('storefront.branches.addWindow')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
